import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import SysColumn from "app/class/sys-column";
import { RecordManagement } from "app/class/record-management";
import queryKeys from "shared/services/queryKeys";
import { useTranslation } from "react-i18next";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";

export interface ChoiceOption {
    id?: string;
    value: string;
    label: string;
    order?: number;
    ref_column?: string;
    ref_table?: string;
}

export const choicesForColumn = (options: ChoiceOption[], column?: SysColumn): ChoiceOption[] => {
    if (!column) return [];
    const matching = options.filter((option) => option.ref_column === column.id);
    const own = matching.filter((option) => option.ref_table === column.sys_table_id);
    if (own.length) return own;
    const ancestor = matching.filter((option) => option.ref_table === column.source_sys_table_id);
    return ancestor.length ? ancestor : matching.filter((option) => !option.ref_table);
};

/**
 * Rótulos para exibir valores já gravados: o conjunto efetivo da coluna (o da filha, se tiver) vem
 * primeiro; valores que só existem no conjunto da mãe (dados antigos) caem para o rótulo da mãe.
 */
export const choiceLabelsForColumn = (options: ChoiceOption[], column?: SysColumn): Map<string, string> => {
    const labels = new Map<string, string>();
    if (!column) return labels;
    const matching = options.filter((option) => option.ref_column === column.id);
    const effective = choicesForColumn(options, column);
    const fallback = [
        ...matching.filter((option) => option.ref_table === column.source_sys_table_id),
        ...matching.filter((option) => !option.ref_table),
    ];
    [...effective, ...fallback].forEach((option) => {
        const key = String(option.value);
        if (!labels.has(key)) labels.set(key, option.label);
    });
    return labels;
};

export const CHOICE_QUERY_KEY = queryKeys.records.lookup("sys_choice", "all");

export const fetchChoiceOptions = async (): Promise<ChoiceOption[]> => {
    const response = await new RecordManagement("sys_choice")
        .paginate(1, 5000)
        .orderBy("order", "asc")
        .run();

    return response.data as unknown as ChoiceOption[];
};

export const useChoiceOptions = (column?: SysColumn) => {
    const { t, i18n } = useTranslation();
    // PRIM-021 (design_prim_021_outcome_vocabulary.md): a VIRTUAL column (e.g. Fluss's synthetic
    // `var.outcome`) can declare its own closed vocabulary inline via SysColumn.options, since it
    // has no real sys_columns.id for a sys_choice row to reference. When present, skip the
    // sys_choice fetch entirely — real catalog columns never set this, so their behavior (query,
    // filter by ref_column) is completely unchanged below.
    const inlineOptions = column?.options;
    const query = useQuery({
        queryKey: CHOICE_QUERY_KEY,
        queryFn: fetchChoiceOptions,
        enabled: Boolean(column?.id && column?.sys_table_id) && !inlineOptions,
        staleTime: 1000 * 60 * 5,
    });

    return {
        ...query,
        data: useMemo(
            () => inlineOptions
                ? translateChoiceOptions(inlineOptions, t)
                : translateChoiceOptions(choicesForColumn(query.data ?? [], column), t),
            [inlineOptions, column?.id, i18n.language, i18n.resolvedLanguage, query.data, t]
        ),
    };
};

export const useChoiceLabelMaps = (columns: SysColumn[]) => {
    const { t, i18n } = useTranslation();
    const choiceColumns = useMemo(
        () => {
            const seen = new Set<string>();
            return columns.filter((column) => {
                const key = `${column.id}:${column.sys_table_id}`;
                if (column.column_type !== "CHOICE" || !column.id || !column.sys_table_id || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        },
        [columns]
    );

    const choiceResult = useQuery({
        queryKey: CHOICE_QUERY_KEY,
        queryFn: fetchChoiceOptions,
        enabled: choiceColumns.length > 0,
        staleTime: 1000 * 60 * 5,
    });

    return useMemo(() => {
        const labelMaps: Record<string, Map<string, string>> = {};
        const tableCountByColumn = new Map<string, number>();
        choiceColumns.forEach(column => tableCountByColumn.set(column.id, (tableCountByColumn.get(column.id) ?? 0) + 1));

        choiceColumns.forEach((column) => {
            const labels = choiceLabelsForColumn(choiceResult.data ?? [], column);
            const labelsForTable = new Map(
                translateChoiceOptions(
                    Array.from(labels, ([value, label]) => ({ value, label })),
                    t
                ).map((option) => [String(option.value), option.label])
            );
            if ((tableCountByColumn.get(column.id) ?? 0) > 1) labelMaps[`${column.id}:${column.sys_table_id}`] = labelsForTable;
            if (!labelMaps[column.id]) labelMaps[column.id] = labelsForTable;
        });

        return labelMaps;
    }, [choiceColumns, choiceResult.data, i18n.language, i18n.resolvedLanguage, t]);
};

const translateChoiceOptions = (
    options: ChoiceOption[],
    t: TFunction
): ChoiceOption[] =>
    options.map((option) => ({
        ...option,
        label: option.label ? resolveDisplayText(t, option.label) : option.label,
    }));
